import { memo, useEffect, useRef, useState } from 'react'
import {
  Bot,
  Check,
  Columns3,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Star,
  User,
  UserPlus,
  Users,
  Voicemail as VoicemailIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { formatPhoneDisplay } from '@/lib/phoneValidation'
import { estIdentiteAgent, libelleCorrespondant, numeroCorrespondant, userIdDeIdentite } from '@/lib/identiteAgent'
import { ouvrirCollegue } from '@/lib/ouvrirConversation'
import type { CallLog } from '@/types/calls'

/**
 * Historique d'appels, bureau : une rangée compacte par appel, en colonnes
 * alignées (modèle Quo). Personne | Résultat | Traité par | Ligne | Durée | Date.
 * La couleur ne sert qu'à « Appel manqué ».
 *
 * Largeurs (liste de 520 px en md, 600 px en xl, 720 px en 2xl) :
 * personne flex ; résultat 128 px ; traité par 160 px et ligne 112 px (xl et
 * plus, faute de place avant) ; durée 40 px ; date 80 px. Chaque utilisateur
 * choisit ses colonnes (menu « Colonnes affichées ») ; une colonne masquée
 * laisse sa place à Personne.
 */
export const COLONNES = {
  resultat: 'w-32 flex-shrink-0',
  traitePar: 'hidden xl:flex w-40 flex-shrink-0',
  ligne: 'hidden xl:flex w-28 flex-shrink-0',
  duree: 'w-10 flex-shrink-0 text-right',
  date: 'w-20 flex-shrink-0 whitespace-nowrap text-right',
}

/** Colonnes que l'utilisateur peut masquer (Personne et Résultat restent). */
export type ColonneOptionnelle = 'traitePar' | 'ligne' | 'duree' | 'date'
export type ColonnesAffichees = Record<ColonneOptionnelle, boolean>
const DEFAUT_COLONNES: ColonnesAffichees = { traitePar: true, ligne: false, duree: true, date: true }
const CLE_COLONNES = 'callizy_historique_colonnes'

/** Le choix des colonnes, gardé dans ce navigateur ; les défauts sinon. */
export function useColonnesHistorique(): [ColonnesAffichees, (c: ColonneOptionnelle, visible: boolean) => void] {
  const [colonnes, setColonnes] = useState<ColonnesAffichees>(() => {
    try {
      const lu = JSON.parse(localStorage.getItem(CLE_COLONNES) || 'null')
      if (lu && typeof lu === 'object') {
        return Object.fromEntries(
          (Object.keys(DEFAUT_COLONNES) as ColonneOptionnelle[]).map((k) => [
            k,
            typeof lu[k] === 'boolean' ? lu[k] : DEFAUT_COLONNES[k],
          ]),
        ) as ColonnesAffichees
      }
    } catch {
      /* stockage illisible ou indisponible : les défauts */
    }
    return DEFAUT_COLONNES
  })
  const changer = (c: ColonneOptionnelle, visible: boolean) =>
    setColonnes((prev) => {
      const suivant = { ...prev, [c]: visible }
      try {
        localStorage.setItem(CLE_COLONNES, JSON.stringify(suivant))
      } catch {
        /* stockage indisponible : le choix vaut pour cette visite */
      }
      return suivant
    })
  return [colonnes, changer]
}

const MANQUES = ['missed', 'no-answer', 'busy', 'failed', 'canceled']

export function estManque(call: CallLog): boolean {
  if (call.type === 'voicemail' || call.has_voicemail) return false
  return (call.direction === 'inbound' || !!call.is_internal) && MANQUES.includes(call.status)
}

/** Heure si aujourd'hui, « Hier », sinon « 24 sept. » (avec l'année si ce n'est pas celle-ci). */
export function dateCourte(iso: string, locale: string, hier: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const maintenant = new Date()
  const jour = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const ecart = Math.round((jour(maintenant) - jour(d)) / 86_400_000)
  if (ecart === 0) return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  if (ecart === 1) return hier
  return d.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() !== maintenant.getFullYear() ? { year: 'numeric' } : {}),
  })
}

function duree(s?: number): string {
  if (!s) return ''
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

function initiales(nom: string): string {
  const mots = nom.replace(/[^\p{L}\p{N}\s]/gu, ' ').trim().split(/\s+/).filter(Boolean)
  if (!mots.length || !/\p{L}/u.test(mots[0][0])) return ''
  return ((mots[0][0] || '') + (mots[1]?.[0] || '')).toUpperCase()
}

function Pastille({ nom, darkMode, taille = 'md' }: { nom: string; darkMode: boolean; taille?: 'sm' | 'md' }) {
  const ini = initiales(nom)
  return (
    <span
      className={cn(
        'flex flex-shrink-0 items-center justify-center rounded-full font-semibold',
        taille === 'md' ? 'h-7 w-7 text-[10.5px]' : 'h-5 w-5 text-[8.5px]',
        darkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-200 text-slate-600',
      )}
    >
      {ini || <User className={taille === 'md' ? 'h-3.5 w-3.5' : 'h-3 w-3'} />}
    </span>
  )
}

interface Props {
  call: CallLog
  darkMode: boolean
  moiId: number | null
  locale: string
  isFav: boolean
  /** Nom de la ligne quand l'entreprise en a plusieurs, sinon null. */
  nomLigne: string | null
  colonnes: ColonnesAffichees
  getDisplayName: (phoneNumber: string | null | undefined) => string
  onOuvrir: (number: string) => void
  onToggleFavorite: (number: string, name: string) => void
  onEnregistrerContact: (id: string, phone: string) => void
}

export const LigneAppel = memo(function LigneAppel({
  call,
  darkMode,
  moiId,
  locale,
  isFav,
  nomLigne,
  colonnes,
  getDisplayName,
  onOuvrir,
  onToggleFavorite,
  onEnregistrerContact,
}: Props) {
  const { t } = useTranslation()
  const vocal = call.type === 'voicemail'
  const numero = vocal ? call.from_number : numeroCorrespondant(call)
  const interne = !vocal && (!!call.is_internal || estIdentiteAgent(numero))
  const brut = vocal ? getDisplayName(numero) : libelleCorrespondant(call, getDisplayName, t('history.agent', 'Agent'))
  const inconnu = !interne && (!brut || brut === numero)
  const nom = inconnu ? formatPhoneDisplay(numero) || numero : brut
  const manque = estManque(call)
  const discret = darkMode ? 'text-slate-500' : 'text-slate-400'
  const texte = darkMode ? 'text-slate-300' : 'text-slate-600'

  // Résultat : icône de direction + libellé ; seul « Appel manqué » est coloré.
  const sortant = call.direction === 'outbound' && (!interne || userIdDeIdentite(call.from_number) === moiId)
  let IconeResultat = sortant ? PhoneOutgoing : PhoneIncoming
  let resultat: string
  if (manque) {
    IconeResultat = PhoneMissed
    resultat = t('history.row.missed', 'Appel manqué')
  } else if (vocal || call.has_voicemail) {
    IconeResultat = VoicemailIcon
    resultat = t('history.row.voicemail', 'Message vocal')
  } else if (call.answer_type === 'ai') {
    // Le nom de l'agent IA est dans « Traité par » : pas de redite ici.
    IconeResultat = Bot
    resultat = t('history.row.aiAgent', 'Agent IA')
  } else if (interne) {
    IconeResultat = Users
    resultat = t('history.row.internal', 'Appel interne')
  } else {
    resultat = sortant ? t('history.row.outbound', 'Sortant') : t('history.row.inbound', 'Entrant')
  }

  // Traité par : qui a appelé, qui a répondu, ou personne.
  // `soi` : c'est la personne connectée (« Vous… »).
  let traitePar: { nom: string; texte: string; ia?: boolean; soi?: boolean } | null = null
  const appelantId = userIdDeIdentite(call.from_number)
  const nomAppelant = call.initiator_agent_name || call.agent_name || ''
  // Le prénom tient dans la colonne (« Noah a répondu ») ; le nom complet
  // reste dans la bulle au survol.
  const prenom = (nom: string) => nom.trim().split(/\s+/)[0] || nom
  if (call.answer_type === 'ai') {
    traitePar = { nom: '', texte: call.agent_name || t('history.row.ai', 'IA'), ia: true }
  } else if (vocal || call.has_voicemail || manque) {
    traitePar = { nom: '', texte: t('history.row.nobody', "Personne n'a répondu") }
  } else if (call.direction === 'outbound') {
    if (interne && appelantId !== moiId && moiId !== null && userIdDeIdentite(call.to_number) === moiId) {
      traitePar = { nom: '', texte: t('history.row.youAnswered', 'Vous avez répondu'), soi: true }
    } else if (appelantId !== null && appelantId === moiId) {
      traitePar = { nom: '', texte: t('history.row.youCalled', 'Vous avez appelé'), soi: true }
    } else if (nomAppelant) {
      traitePar = { nom: nomAppelant, texte: t('history.row.called', '{{name}} a appelé', { name: prenom(nomAppelant) }) }
    }
  } else if (call.answered_by_user != null && call.answered_by_user === moiId) {
    traitePar = { nom: '', texte: t('history.row.youAnswered', 'Vous avez répondu'), soi: true }
  } else if (call.agent_name) {
    traitePar = { nom: call.agent_name, texte: t('history.row.answered', '{{name}} a répondu', { name: prenom(call.agent_name) }) }
  }
  // Ancien appel sans trace de qui l'a passé ou pris : on le dit, sans laisser vide.
  if (!traitePar) traitePar = { nom: '', texte: t('history.row.unknown', 'Inconnu') }
  const moi = !!traitePar?.soi

  // Appel interne : le clic ouvre le fil du collègue ; sinon la conversation.
  const collegueId = interne
    ? appelantId === moiId
      ? userIdDeIdentite(call.to_number)
      : appelantId
    : null
  const ouvrir = () => {
    if (interne) {
      if (collegueId) ouvrirCollegue(collegueId)
    } else onOuvrir(numero)
  }

  const nonLu = vocal && !call.is_read
  // Actions du survol : enregistrer un inconnu, favori (pas pour un collègue).
  const aDesActions = inconnu || !interne

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={ouvrir}
      onKeyDown={(e) => {
        if (e.key === 'Enter') ouvrir()
      }}
      className={cn(
        'group relative flex h-11 cursor-pointer items-center gap-2.5 px-3 text-[13px] transition-colors',
        darkMode ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50',
      )}
    >
      {/* Personne */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span className="relative">
          <Pastille nom={inconnu ? '' : nom} darkMode={darkMode} />
          {nonLu && <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-red-500" />}
        </span>
        <span
          className={cn(
            'truncate',
            nonLu ? 'font-semibold' : 'font-medium',
            darkMode ? 'text-slate-100' : 'text-slate-900',
          )}
        >
          {nom}
        </span>
        {isFav && <Star className="h-3 w-3 flex-shrink-0 fill-current text-amber-400" />}
        {!!call.pending_tasks_count && (
          <span
            className={cn(
              'inline-flex h-4 min-w-4 flex-shrink-0 items-center justify-center rounded-full px-1 text-[9px] font-bold',
              darkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700',
            )}
          >
            {call.pending_tasks_count}
          </span>
        )}
      </div>

      {/* Résultat */}
      <div
        className={cn(
          COLONNES.resultat,
          'flex min-w-0 items-center gap-1.5 text-[12px]',
          manque ? (darkMode ? 'text-red-400 font-medium' : 'text-red-600 font-medium') : texte,
        )}
      >
        <IconeResultat className={cn('h-3.5 w-3.5 flex-shrink-0', !manque && discret)} />
        <span className="truncate">{resultat}</span>
      </div>

      {/* Traité par */}
      {colonnes.traitePar && (
      <div className={cn(COLONNES.traitePar, 'min-w-0 items-center gap-1.5 text-[12px]', texte)}>
        {traitePar?.ia ? (
          <span
            className={cn(
              'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full',
              darkMode ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-600',
            )}
          >
            <Bot className="h-3 w-3" />
          </span>
        ) : traitePar?.nom ? (
          <Pastille nom={traitePar.nom} darkMode={darkMode} taille="sm" />
        ) : moi ? (
          <span
            className={cn(
              'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full',
              darkMode ? 'bg-indigo-500/25 text-indigo-200' : 'bg-indigo-100 text-indigo-600',
            )}
          >
            <User className="h-3 w-3" />
          </span>
        ) : null}
        <span
          className={cn('truncate', !traitePar?.nom && !moi && !traitePar?.ia && discret)}
          title={traitePar?.nom || traitePar?.texte}
        >
          {traitePar?.texte ?? ''}
        </span>
      </div>
      )}

      {/* Ligne (au choix), seulement avec plusieurs lignes ; nom complet au survol */}
      {colonnes.ligne && (
        <div className={cn(COLONNES.ligne, 'min-w-0 items-center')}>
          {nomLigne && (
            <span
              title={nomLigne}
              className={cn(
                'block max-w-full truncate rounded px-1.5 py-0.5 text-[11px] font-medium',
                darkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-600',
              )}
            >
              {nomLigne}
            </span>
          )}
        </div>
      )}

      {/* Durée */}
      {colonnes.duree && (
        <span className={cn(COLONNES.duree, 'text-[12px] tabular-nums', discret)}>
          {duree(vocal ? call.recording_duration : call.duration)}
        </span>
      )}

      {/* Date ; au survol, les actions prennent sa place s'il y en a */}
      {colonnes.date && (
        <span className={cn(COLONNES.date, 'text-[12px] tabular-nums', aDesActions && 'group-hover:invisible', discret)}>
          {dateCourte(call.started_at || call.created_at || '', locale, t('history.row.yesterday', 'Hier'))}
        </span>
      )}
      {aDesActions && (
      <div
        className={cn(
          'absolute right-2 top-1/2 hidden -translate-y-1/2 items-center gap-0.5 rounded-md px-0.5 group-hover:flex',
          darkMode ? 'bg-slate-800' : 'bg-slate-50',
        )}
      >
        {inconnu && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onEnregistrerContact(`${vocal ? 'vm' : 'call'}-${call.id}`, numero)
            }}
            className={cn(
              'rounded-full p-1 transition-colors',
              darkMode ? 'text-slate-400 hover:bg-slate-700 hover:text-indigo-300' : 'text-slate-400 hover:bg-indigo-50 hover:text-indigo-600',
            )}
            title={t('history.row.saveContact', 'Enregistrer comme contact')}
          >
            <UserPlus className="h-3.5 w-3.5" />
          </button>
        )}
        {!interne && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onToggleFavorite(numero, inconnu ? '' : nom)
            }}
            className={cn(
              'rounded-full p-1 transition-colors',
              isFav
                ? 'text-amber-400'
                : darkMode
                  ? 'text-slate-400 hover:bg-slate-700 hover:text-amber-400'
                  : 'text-slate-400 hover:bg-slate-100 hover:text-amber-500',
            )}
            title={
              isFav
                ? t('history.removeFromFavorites', 'Retirer des favoris')
                : t('history.addToFavorites', 'Ajouter aux favoris')
            }
          >
            <Star className={cn('h-3.5 w-3.5', isFav && 'fill-current')} />
          </button>
        )}
      </div>
      )}
    </div>
  )
})

/** En-tête des colonnes, au-dessus de la liste ; le bouton de gauche ouvre
 * le menu « Colonnes affichées » (modèle du menu des filtres de la boîte). */
export function EnteteColonnes({
  darkMode,
  avecLigne,
  colonnes,
  onChanger,
}: {
  darkMode: boolean
  /** L'entreprise a plusieurs lignes : la colonne Ligne est proposée. */
  avecLigne: boolean
  colonnes: ColonnesAffichees
  onChanger: (c: ColonneOptionnelle, visible: boolean) => void
}) {
  const { t } = useTranslation()
  const [ouvert, setOuvert] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!ouvert) return
    const fermer = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOuvert(false)
    }
    const echap = (e: KeyboardEvent) => e.key === 'Escape' && setOuvert(false)
    document.addEventListener('mousedown', fermer)
    document.addEventListener('keydown', echap)
    return () => {
      document.removeEventListener('mousedown', fermer)
      document.removeEventListener('keydown', echap)
    }
  }, [ouvert])

  const libelles: { cle: ColonneOptionnelle | 'personne' | 'resultat'; nom: string }[] = [
    { cle: 'personne', nom: t('history.col.person', 'Personne') },
    { cle: 'resultat', nom: t('history.col.result', 'Résultat') },
    { cle: 'traitePar', nom: t('history.col.handledBy', 'Traité par') },
    ...(avecLigne ? [{ cle: 'ligne' as const, nom: t('history.col.line', 'Ligne') }] : []),
    { cle: 'duree', nom: t('history.col.duration', 'Durée') },
    { cle: 'date', nom: t('history.col.date', 'Date') },
  ]

  return (
    <div
      className={cn(
        'relative flex h-7 flex-shrink-0 items-center gap-2.5 border-b px-3 text-[10.5px] font-semibold uppercase tracking-wider',
        darkMode ? 'border-slate-800 text-slate-500' : 'border-slate-100 text-slate-400',
      )}
    >
      {/* Même place que l'avatar des rangées : les titres restent alignés. */}
      <div ref={menuRef} className="relative flex min-w-0 flex-1 items-center gap-2">
        <button
          type="button"
          onClick={() => setOuvert((o) => !o)}
          title={t('history.columnsMenu.title', 'Colonnes affichées')}
          aria-label={t('history.columnsMenu.title', 'Colonnes affichées')}
          aria-expanded={ouvert}
          className={cn(
            'flex h-6 w-7 flex-shrink-0 items-center justify-center rounded transition-colors',
            ouvert
              ? darkMode
                ? 'bg-slate-800 text-white'
                : 'bg-slate-100 text-slate-700'
              : darkMode
                ? 'text-slate-500 hover:bg-slate-800 hover:text-slate-300'
                : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600',
          )}
        >
          <Columns3 className="h-3.5 w-3.5" />
        </button>
        <span className="truncate">{t('history.col.person', 'Personne')}</span>

        {ouvert && (
          <div
            className={cn(
              'absolute left-0 top-full z-50 mt-1 w-52 rounded-lg border py-1 normal-case tracking-normal shadow-lg',
              darkMode ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white',
            )}
          >
            <p className={cn('px-3 pb-1.5 pt-1 text-[10.5px] font-semibold uppercase tracking-wider', darkMode ? 'text-slate-500' : 'text-slate-400')}>
              {t('history.columnsMenu.title', 'Colonnes affichées')}
            </p>
            {libelles.map(({ cle, nom }) => {
              const fixe = cle === 'personne' || cle === 'resultat'
              const coche = fixe || colonnes[cle as ColonneOptionnelle]
              return (
                <button
                  key={cle}
                  type="button"
                  role="checkbox"
                  aria-checked={coche}
                  disabled={fixe}
                  onClick={() => !fixe && onChanger(cle as ColonneOptionnelle, !coche)}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-1 text-left text-[12px] font-normal disabled:cursor-default',
                    darkMode ? 'text-slate-300 enabled:hover:bg-slate-800' : 'text-slate-700 enabled:hover:bg-slate-50',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
                      coche
                        ? 'border-indigo-600 bg-indigo-600 text-white'
                        : darkMode
                          ? 'border-slate-600'
                          : 'border-slate-300',
                      fixe && 'opacity-50',
                    )}
                  >
                    {coche && <Check className="h-3 w-3" />}
                  </span>
                  {nom}
                </button>
              )
            })}
          </div>
        )}
      </div>
      <span className={COLONNES.resultat}>{t('history.col.result', 'Résultat')}</span>
      {colonnes.traitePar && <span className={COLONNES.traitePar}>{t('history.col.handledBy', 'Traité par')}</span>}
      {colonnes.ligne && <span className={COLONNES.ligne}>{t('history.col.line', 'Ligne')}</span>}
      {colonnes.duree && <span className={COLONNES.duree}>{t('history.col.duration', 'Durée')}</span>}
      {colonnes.date && <span className={COLONNES.date}>{t('history.col.date', 'Date')}</span>}
    </div>
  )
}
